
import React, { useState, useRef, useEffect } from 'react';
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Check, X, Edit3 } from "lucide-react";
import { cn } from "@/lib/utils";

interface EditableCellProps {
  row: any;
  field: string;
  value?: string | number;
  onSave?: (value: string | number) => Promise<void>;
  placeholder?: string;
  inputType?: "text" | "number" | "url" | "date";
  className?: string;
  disabled?: boolean;
  isPending?: (id: string, field?: string) => boolean;
  applyOptimisticUpdate?: (rowId: string, field: string, value: any) => void;
  isOptimisticProcessing?: boolean;
}

export const EditableCell: React.FC<EditableCellProps> = ({
  row,
  field,
  value: propValue,
  onSave,
  placeholder = "Enter value",
  inputType = "text",
  className = "",
  disabled = false,
  isPending,
  applyOptimisticUpdate,
  isOptimisticProcessing
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanged, setHasChanged] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Get the actual value from row data or prop
  const actualValue = propValue !== undefined ? propValue : (row ? row[field] : '');
  const displayValue = actualValue?.toString() || '';

  // Update edit value when prop value changes
  useEffect(() => {
    if (!isEditing) {
      setEditValue(displayValue);
    }
  }, [displayValue, isEditing]);

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleStartEdit = () => {
    if (disabled) return;
    setIsEditing(true);
    setEditValue(displayValue);
    setHasChanged(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditValue(displayValue);
    setHasChanged(false);
  };

  const handleSave = async () => {
    if (disabled) return;
    if (isSaving) return;

    try {
      setIsSaving(true);
      
      let finalValue: string | number = editValue;
      if (inputType === 'number') {
        finalValue = editValue === '' ? 0 : Number(editValue);
      }

      // Use optimistic update if available, otherwise use onSave
      if (applyOptimisticUpdate && row) {
        applyOptimisticUpdate(row.id, field, finalValue);
      } else if (onSave) {
        await onSave(finalValue);
      }
      
      setIsEditing(false);
      setHasChanged(false);
    } catch (error) {
      console.error('Failed to save:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setEditValue(newValue);
    setHasChanged(newValue !== displayValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  const handleInputClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleClick = () => {
    if (!isEditing && !disabled) {
      setIsEditing(true);
    }
  };

  const formatValue = (value: string | number) => {
    if (typeof value === 'number') {
      return value.toLocaleString();
    }
    return value;
  };

  const isEmpty = !displayValue;
  const isCurrentlyPending = isPending && row ? isPending(row.id, field) : false;

  if (disabled) {
    // Show read-only version when disabled
    return (
      <div className={cn("py-2 px-3 text-muted-foreground", className)}>
        <div className="truncate" title={formatValue(actualValue)}>
          {formatValue(actualValue)}
        </div>
      </div>
    );
  }

  if (!isEditing) {
    return (
      <div 
        className={cn(
          "group flex items-center gap-2 min-h-[32px] px-2 py-1 rounded cursor-pointer",
          "hover:bg-muted/50 transition-colors",
          disabled && "cursor-not-allowed opacity-50",
          isCurrentlyPending && "opacity-50",
          className
        )}
        onClick={handleClick}
      >
        <span className={cn(
          "flex-1 text-xs truncate",
          isEmpty && "text-muted-foreground italic"
        )}>
          {isEmpty ? placeholder : displayValue}
        </span>
        {!disabled && (
          <Edit3 className="h-3 w-3 opacity-0 group-hover:opacity-50 transition-opacity flex-shrink-0" />
        )}
      </div>
    );
  }

  return (
    <div className={cn(
      "flex items-center gap-1 p-1 bg-background border rounded",
      "ring-2 ring-primary/20 border-primary/30",
      className
    )}>
      <Input
        ref={inputRef}
        type={inputType}
        value={editValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onClick={handleInputClick}
        placeholder={placeholder}
        disabled={isSaving}
        className={cn(
          "h-6 text-xs border-0 bg-transparent focus-visible:ring-0 px-1 flex-1",
          hasChanged && "text-amber-600 dark:text-amber-400"
        )}
      />
      <div className="flex gap-1 flex-shrink-0">
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={handleSave}
          disabled={isSaving || !hasChanged}
          className={cn(
            "h-5 w-5 p-0 hover:bg-green-100 hover:text-green-700",
            "dark:hover:bg-green-900 dark:hover:text-green-300",
            hasChanged && "text-green-600 dark:text-green-400"
          )}
        >
          <Check className="h-3 w-3" />
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={handleCancel}
          disabled={isSaving}
          className="h-5 w-5 p-0 hover:bg-red-100 hover:text-red-700 dark:hover:bg-red-900 dark:hover:text-red-300"
        >
          <X className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
};
